import React, { Fragment, useCallback } from "react";
import { Pressable, Text, View } from "react-native";
import { Button } from "@/elements/components/ui/button";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import { Avatar } from "../ui/avatar";
import RemoteIcon from "@/assets/svgs/remote-icon";
import UserIcon from "@/assets/svgs/user-icon";
import ClockIcon from "@/assets/svgs/clock-icon";
import { api } from "../utils/convex-api";
import { useSheet } from "./sheet-provider";
import { SheetIds } from "../utils/sheet-ids";
import { LessonSheetPayload, LessonSheetResult } from "../sheets/lesson-sheet";
import { useRouter } from "expo-router";

function StudentSessionCard({
  session,
}: {
  session: (typeof api.student.home.queries.weeklySessions._returnType)[0];
}) {
  const fmt = useFormatDate();
  const isSessionInProgress = session.status === "in_progress";
  const isSessionCompleted = session.status === "completed";
  const isSessionNoShow = session.status === "no_show_reported_by_student" ||
    session.status === "no_show_reported_by_tutor";
  const { open } = useSheet<LessonSheetPayload, LessonSheetResult>(
    SheetIds.LESSON
  );
  const router = useRouter();

  const handleOpenLessonSheet = useCallback(async () => {
    const result = await open({
      lessonData: session,
      isStudent: true,
    });
    if (result?.reportTutorNoShow) {
      router.push(`/(app)/report-tutor-no-show?lessonId=${session._id}`);
    }
  }, [open, session, router]);

  const handleJoinLesson = useCallback(() => {
    if (session.deliveryMethod === "remote") {
      if (!session.url) throw new Error('session url expected');
      location.href = session.url;
    } else {
      router.push(`/(app)/lesson-recording/${session._id}`);
    }
  }, [session._id, session.deliveryMethod, session.url]);

  return (
    <Pressable
      key={session._id}
      className={`p-4 rounded-[17px] gap-3 active:opacity-80 ${
        isSessionInProgress
          ? "bg-teachLoop-accentPrimary"
          : isSessionCompleted
            ? "bg-teachLoop-accentPrimaryDisabled"
            : "bg-[#FFFDFB]"
      }`}
      style={{
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
        elevation: 2,
      }}
      onPress={handleOpenLessonSheet}
      disabled={false}
    >
      {isSessionNoShow && (
        <View className="mb-2">
          <Text className="font-geologica-medium font-medium text-sm text-red-600">
            No Show
          </Text>
        </View>
      )}
      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center gap-2">
          <Text
            className={`font-geologica-medium font-medium text-base ${
              isSessionInProgress ? "text-white" : "text-gray-800"
            } leading-[1.5]`}
          >
            {session.subject}
          </Text>
        </View>
        <Fragment>
          {isSessionInProgress ? (
            <Button
              className="bg-white"
              size="sm"
              icon
              onPress={handleJoinLesson}
              disabled={session.deliveryMethod === "remote" && !session.url}
            >
              <View className="w-[9px] h-[9px] bg-green-400 rounded-full items-center justify-center" />
              <Button.Text intent="primary" size="sm">
                {session.deliveryMethod === "remote"
                  ? "Join lesson"
                  : "Start lesson"}
              </Button.Text>
            </Button>
          ) : (
            <Text className="font-geologica-medium font-medium text-base text-gray-800 leading-[1.2]">
              {fmt(session.startTime, "do MMMM")}
            </Text>
          )}
        </Fragment>
      </View>

      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center gap-2">
          <Avatar size="sm">
            <Avatar.Content
              label={session.tutor.firstName}
              labelSize="sm"
              size="sm"
              source={
                session.tutor.imageUrl
                  ? { uri: session.tutor.imageUrl }
                  : undefined
              }
            />
          </Avatar>
          <Text
            className={`font-geologica-regular font-normal ${
              isSessionInProgress ? "text-white" : "text-gray-800"
            } text-sm leading-[1.5]`}
          >
            {session.tutor.firstName}
          </Text>
        </View>

        <View className="flex-row items-center gap-[6px]">
          {session.deliveryMethod === "in_person" ? (
            <UserIcon
              className="size-4"
              pathClassName={
                isSessionInProgress ? "stroke-white" : "stroke-gray-800"
              }
            />
          ) : (
            <RemoteIcon
              className="size-4"
              pathClassName={
                isSessionInProgress ? "stroke-white" : "stroke-gray-800"
              }
            />
          )}
          <Text
            className={`font-geologica-regular font-normal ${
              isSessionInProgress ? "text-white" : "text-gray-800"
            } text-sm leading-[1.5]`}
          >
            {session.deliveryMethod === "in_person" ? "In person" : "Remote"}
          </Text>
        </View>

        <View className="flex-row items-center gap-2">
          <ClockIcon
            className="size-4"
            pathClassName={
              isSessionInProgress ? "stroke-white" : "stroke-gray-800"
            }
          />
          <Text
            className={`font-geologica-regular font-normal ${
              isSessionInProgress ? "text-white" : "text-gray-800"
            } text-sm leading-[1.5]`}
          >
            {fmt(session.startTime, "HH:mm")} -{" "}
            {fmt(session.endTime, "HH:mm")}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

export default StudentSessionCard;
